import {
	createMRTColumnHelper,
	MRT_ColumnDef,
	MRT_Row,
} from 'material-react-table'
import { memo, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import {
	CustomSelectOption,
	ExpandableTextCell,
	getMrtTableDisabled,
	MonacoScriptCell,
	SelectCell,
	TextCell,
} from '@/components'
import { createHeaderWithTooltip, DisabledCell } from '@/components/MrtTable'
import { getAvailableStructureForSourceObject } from '@/endpoints'
import { useApi } from '@/endpoints/hooks'
import { SourceEnums } from '@/endpoints/schemas'
import { useAppContext } from '@/hooks'
import { UpdateCellValueHandler } from '@/hooks/useTableHandlers'
import { CustomOption } from '@/lib/components/Select/CustomSelectComponents'
import { Tooltip } from '@/lib/components/Tooltip/Tooltip'
import {
	createTooltipHandlers,
	releaseActiveTooltip,
} from '@/lib/components/Tooltip/utils'
import { useDetailTabContext } from '@/pages/User/pages/Home/components/DetailTab'
import { useGetSystemUsersUsingGetQuery } from '@/rtkApi'
import { SourceItem } from '@/rtkApi/mddApiSliceGeneratedV2'
import {
	findStructureForSourceItem,
	getAvailableStructures,
	getSourceObjectUuid,
	useSourceOptions,
} from '@/utils/source'

const MIN_TOOLTIP_LENGTH = 13

type ReadOnlyCellWithTooltipProps = {
	code: string,
	fullPath: string,
	name: string,
	path: string
}

const ReadOnlyCellWithTooltip = memo(
	({ name, path, fullPath, code }: ReadOnlyCellWithTooltipProps) => {
		const tooltipTitle = `${fullPath} (${code})`
		const showTooltip = tooltipTitle.length > MIN_TOOLTIP_LENGTH
		const [tooltipState, setTooltipState] = useState({
			show: false,
			position: { top: 0, left: 0 },
		})
		const hideRef = useRef<() => void>(() => undefined)
		const stableHide = useRef(() => {
			hideRef.current()
		}).current

		hideRef.current = () => {
			releaseActiveTooltip(stableHide)
			setTooltipState((prev) =>
				prev.show ? { ...prev, show: false } : prev,
			)
		}

		useEffect(() => {
			return () => {
				stableHide()
			}
		}, [stableHide])

		const { handleMouseEnter, handleMouseLeave } = createTooltipHandlers(
			showTooltip ? tooltipTitle : undefined,
			{
				showTooltip: tooltipState.show,
				tooltipPosition: tooltipState.position,
				isMenuOpen: false,
			},
			{
				setTooltipPosition: (pos) =>
					setTooltipState((prev) => ({ ...prev, position: pos })),
				setShowTooltip: (show) =>
					setTooltipState((prev) => ({ ...prev, show })),
			},
			stableHide,
		)

		return (
			<div className="flex items-center h-full w-full">
				<div
					className="w-full"
					onMouseEnter={handleMouseEnter}
					onMouseLeave={handleMouseLeave}
				>
					<span className="px-2 truncate block">
						{name}
						<span className="text-xs opacity-70 ml-1">({path})</span>
					</span>
				</div>
				{createPortal(
					<Tooltip
						showTooltip={tooltipState.show}
						title={showTooltip ? tooltipTitle : undefined}
						isMenuOpen={false}
						tooltipPosition={tooltipState.position}
					/>,
					document.body,
				)}
			</div>
		)
	},
)

type UseSourceColumnsV2Props = {
	updateCellValue: UpdateCellValueHandler<SourceItem>
}

export const useSourceColumnsV2 = ({ updateCellValue }: UseSourceColumnsV2Props) => {
	const { t } = useAppContext()
	const {
		state: { node, systemNodeId, editMode },
	} = useDetailTabContext()

	// Always use getAvailableStructureForSourceObject to get full structure data for display
	const systemTables = useApi(
		getAvailableStructureForSourceObject(node.id, 'SYSTEM'),
	)

	const { data: systemUsers, isLoading: isSystemUserLoading } =
		useGetSystemUsersUsingGetQuery({ structureId: systemNodeId })

	const { sourceObjectTypes, sourceStereotypes } = useSourceOptions()

	const columnHelper = createMRTColumnHelper<SourceItem>()

	const columns = useMemo(
		() => [
			columnHelper.accessor('stereotype', {
				...createHeaderWithTooltip(t('MAPPING_SOURCES_STEREOTYPE')),
				size: 120,
				minSize: 100,
				meta: {
					optionsSource: sourceStereotypes || [],
					optionValue: 'value',
					optionLabel: 'label',
					isNumeric: true,
				},
				Edit: ({ cell, table, row, column }) => {
					const isDisabled = getMrtTableDisabled<SourceItem>({
						editMode,
						row,
						column,
					})

					return (
						<SelectCell<SourceItem>
							cell={cell}
							column={column}
							table={table}
							row={row}
							updateCellValue={updateCellValue}
							isDisabled={isDisabled}
						/>
					)
				},
			}),
			columnHelper.accessor('objectType', {
				...createHeaderWithTooltip(t('MAPPING_SOURCES_OBJECT_TYPE')),
				size: 100,
				minSize: 80,
				meta: {
					optionsSource: sourceObjectTypes || [],
					optionValue: 'value',
					optionLabel: 'label',
					isNumeric: true,
				},
				Edit: ({ cell, table, row, column }) => {
					const isDisabled = getMrtTableDisabled<SourceItem>({
						editMode,
						row,
						column,
					})

					return (
						<SelectCell<SourceItem>
							cell={cell}
							column={column}
							table={table}
							row={row}
							updateCellValue={updateCellValue}
							isDisabled={isDisabled}
						/>
					)
				},
			}),
		columnHelper.accessor(
			(row) =>
				systemTables.data
					? getSourceObjectUuid(row, systemTables.data)
					: row.objectUuid || '',
			{
				id: 'objectUuid',
				...createHeaderWithTooltip(t('MAPPING_SOURCES_OBJECT')),
				size: 200,
				minSize: 150,
				meta: {
					optionsSource: (row: SourceItem) =>
						getAvailableStructures({
							sourceItemRow: row,
							systemTables: systemTables.data,
						}),
					optionValue: 'structureUuid',
					optionLabel: 'name',
					isNumeric: false,
					// react-select OptionProps; typed as any to avoid pulling in full SelectComponents typings
					customSelectOptions: (props: any) => <CustomSelectOption {...props} />,
				},
				// Read-only display: show "name (path)"
				Cell: ({ row }) => {
					const availableOptions = getAvailableStructures({
						sourceItemRow: row.original,
						systemTables: systemTables.data,
					})

					const selectedOption = findStructureForSourceItem(
						row.original,
						availableOptions ?? [],
					)

					if (selectedOption) {
						return `${selectedOption.name} (${selectedOption.fullPath})`
					}

					return row.original.objectCode || '-'
				},
				Edit: ({ cell, table, row, column }) => {
						const isLoading = systemTables.loading

						const isDisabled = getMrtTableDisabled<SourceItem>({
							editMode,
							row,
							column,
						})

						const availableOptions = getAvailableStructures({
							sourceItemRow: row.original,
							systemTables: systemTables.data,
						})

						const selectedOption = findStructureForSourceItem(
							row.original,
							availableOptions ?? [],
						)

						// In read-only mode, show "name (path)" with styled path and tooltip
						if (isDisabled) {
							if (selectedOption) {
								// Trim the path to remove the last segment (the name itself)
								const trimmedPath = selectedOption.fullPath?.includes('>')
									? selectedOption.fullPath.substring(0, selectedOption.fullPath.lastIndexOf('>'))
									: selectedOption.fullPath
								
								return (
									<ReadOnlyCellWithTooltip
										name={selectedOption.name}
										path={trimmedPath || ''}
										fullPath={selectedOption.fullPath || ''}
										code={selectedOption.code || ''}
									/>
								)
							}
							
							return (
								<span className="px-2 truncate block">
									{row.original.objectCode || '-'}
								</span>
							)
						}

						const title = selectedOption
							? `${selectedOption.fullPath} (${selectedOption.code})`
							: undefined

						return (
							<SelectCell
								cell={cell}
								column={column}
								table={table}
								row={row}
								updateCellValue={updateCellValue}
								title={title}
								isLoading={isLoading}
								isDisabled={isDisabled}
								components={{ Option: CustomOption }}
								customValue={getSourceObjectUuid(row.original, systemTables.data)}
							/>
						)
					},
				},
			),
			columnHelper.accessor('ownerId', {
				...createHeaderWithTooltip(t('NODE_OWNER')),
				size: 120,
				minSize: 100,
				meta: {
					optionsSource: systemUsers || [],
					optionValue: 'id',
					optionLabel: 'name',
					isLoading: isSystemUserLoading,
					isNumeric: true,
				},
				Edit: ({ cell, table, row, column }) => {
					const isDisabled = getMrtTableDisabled<SourceItem>({
						editMode,
						row,
						column,
					})

					return (
						<SelectCell
							cell={cell}
							column={column}
							table={table}
							row={row}
							updateCellValue={updateCellValue}
							isDisabled={isDisabled}
						/>
					)
				},
			}),
			columnHelper.accessor('objectCustom', {
				...createHeaderWithTooltip(t('SOURCE_SUBSELECT')),
				size: 150,
				minSize: 100,
				meta: {
					isDisabled: (row: MRT_Row<SourceItem>) =>
						row.original.objectType !== SourceEnums.ObjectTypeEnum.SUB_SELECT,
				},
				Edit: ({ cell, table: _table, row, column }) => {
					const isDisabled = getMrtTableDisabled<SourceItem>({
						editMode,
						row,
						column,
					})

					if (
						row.original.objectType !== SourceEnums.ObjectTypeEnum.SUB_SELECT
					) {
						return <DisabledCell />
					}

					return (
						<MonacoScriptCell
							cell={cell}
							column={column}
							row={row}
							updateCellValue={updateCellValue}
							isDisabled={isDisabled}
							isReadOnly={!editMode}
							language="sql"
						/>
					)
				},
			}),
			columnHelper.accessor('alias', {
				...createHeaderWithTooltip(t('MAPPING_SOURCES_ALIAS')),
				size: 100,
				minSize: 80,
				Edit: ({ cell, table, row, column }) => {
					const isDisabled = getMrtTableDisabled<SourceItem>({
						editMode,
						row,
						column,
					})

					return (
						<TextCell
							cell={cell}
							column={column}
							table={table}
							row={row}
							updateCellValue={updateCellValue}
							isDisabled={isDisabled}
						/>
					)
				},
			}),
			columnHelper.accessor('expression', {
				...createHeaderWithTooltip(t('MAPPING_SOURCES_EXPRESSION')),
				size: 150,
				minSize: 100,
				Edit: ({ cell, table: _table, row, column }) => {
					const isDisabled = getMrtTableDisabled<SourceItem>({
						editMode,
						row,
						column,
					})

					return (
						<MonacoScriptCell
							cell={cell}
							column={column}
							row={row}
							updateCellValue={updateCellValue}
							isDisabled={isDisabled}
							isReadOnly={!editMode}
							language="sql"
						/>
					)
				},
			}),
			columnHelper.accessor('sourceContext', {
				...createHeaderWithTooltip(t('MAPPING_SOURCES_SOURCE_CONTEXT')),
				size: 200,
				minSize: 150,
				Edit: ({ cell, row, column }) => {
					const isDisabled = getMrtTableDisabled<SourceItem>({
						editMode,
						row,
						column,
					})

					return (
						<ExpandableTextCell
							cell={cell}
							column={column}
							row={row}
							updateCellValue={updateCellValue}
							isDisabled={isDisabled}
							isReadOnly={!editMode}
						/>
					)
				},
			}),
			// MDD-2603: Description and Comment per source row
			columnHelper.accessor('description', {
				...createHeaderWithTooltip(t('DESCRIPTION')),
				size: 200,
				minSize: 150,
				Edit: ({ cell, row, column }) => {
					const isDisabled = getMrtTableDisabled<SourceItem>({
						editMode,
						row,
						column,
					})

					return (
						<ExpandableTextCell
							cell={cell}
							column={column}
							row={row}
							updateCellValue={updateCellValue}
							isDisabled={isDisabled}
							isReadOnly={!editMode}
						/>
					)
				},
			}),
			columnHelper.accessor('comment', {
				...createHeaderWithTooltip(t('VIEW_COMMENT')),
				size: 200,
				minSize: 150,
				Edit: ({ cell, row, column }) => {
					const isDisabled = getMrtTableDisabled<SourceItem>({
						editMode,
						row,
						column,
					})

					return (
						<ExpandableTextCell
							cell={cell}
							column={column}
							row={row}
							updateCellValue={updateCellValue}
							isDisabled={isDisabled}
							isReadOnly={!editMode}
						/>
					)
				},
			}),
		],
		[
			columnHelper,
			t,
			sourceStereotypes,
			sourceObjectTypes,
			systemUsers,
			isSystemUserLoading,
			editMode,
			updateCellValue,
			systemTables.data,
			systemTables.loading,
		],
	)

	return columns as MRT_ColumnDef<SourceItem>[]
}
